import React from 'react';
import { useIntl } from 'react-intl';
import {
  Card, CardBody, CardFooter, CardHeader,
} from 'reactstrap';

import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import Heading from 'src/enosikit/components/Heading';

/**
 * Loading skeleton for the Profile Settings page.
 * Mirrors the layout of `ProfileSettings/index.jsx`: breadcrumbs, heading
 * (with a skeleton subtitle in place of the user's email), and a form card
 * skeleton with three field groups and a footer action.
 * @returns {React.ReactElement} Placeholder component.
 */
function Placeholder() {
  const intl = useIntl();

  const breadcrumbsProfile = intl.formatMessage({
    id: 'profile.profile_settings.placeholder.breadcrumbs.profile',
    defaultMessage: 'Profile',
  });
  const breadcrumbsSettings = intl.formatMessage({
    id: 'profile.profile_settings.placeholder.breadcrumbs.settings',
    defaultMessage: 'Settings',
  });
  const headingProfile = intl.formatMessage({
    id: 'profile.profile_settings.placeholder.heading.profile',
    defaultMessage: 'Profile',
  });
  const headingSettings = intl.formatMessage({
    id: 'profile.profile_settings.placeholder.heading.settings',
    defaultMessage: 'Settings',
  });
  const cardHeader = intl.formatMessage({
    id: 'profile.profile_settings.placeholder.card.header',
    defaultMessage: 'Profile',
  });
  const emailLabel = intl.formatMessage({
    id: 'profile.profile_settings.placeholder.email.label',
    defaultMessage: 'Email',
  });
  const givenNameLabel = intl.formatMessage({
    id: 'profile.profile_settings.placeholder.given_name.label',
    defaultMessage: 'Given name',
  });
  const familyNameLabel = intl.formatMessage({
    id: 'profile.profile_settings.placeholder.family_name.label',
    defaultMessage: 'Family name',
  });

  return (
    <>
      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbsProfile, path: '/profile/settings' },
          { name: breadcrumbsSettings },
        ]}
      />
      <Heading
        title={headingProfile}
        context={headingSettings}
        subtitle={<span className="placeholder placeholder-glow bg-darken" style={{ width: '12rem' }} />}
      />

      {/* Form card skeleton */}
      <Card className="mt-4 mb-4" aria-hidden="true">
        <CardHeader tag="h5">{cardHeader}</CardHeader>
        <CardBody>
          {/* Intro line */}
          <div className="mb-3">
            <span className="placeholder placeholder-glow bg-darken col-6" />
          </div>

          {/* Email field */}
          <div className="mb-3">
            <div className="mb-1">
              <span className="placeholder placeholder-glow bg-darken" style={{ width: `${emailLabel.length * 0.55}rem` }} />
            </div>
            <span className="placeholder placeholder-glow bg-darken col-12" style={{ height: '2.375rem', borderRadius: '0.25rem' }} />
            <div className="mt-1">
              <span className="placeholder placeholder-glow bg-darken col-10" style={{ height: '0.75rem' }} />
            </div>
          </div>

          {/* Given name field */}
          <div className="mb-3">
            <div className="mb-1">
              <span className="placeholder placeholder-glow bg-darken" style={{ width: `${givenNameLabel.length * 0.55}rem` }} />
            </div>
            <span className="placeholder placeholder-glow bg-darken col-12" style={{ height: '2.375rem', borderRadius: '0.25rem' }} />
          </div>

          {/* Family name field */}
          <div className="mb-3">
            <div className="mb-1">
              <span className="placeholder placeholder-glow bg-darken" style={{ width: `${familyNameLabel.length * 0.55}rem` }} />
            </div>
            <span className="placeholder placeholder-glow bg-darken col-12" style={{ height: '2.375rem', borderRadius: '0.25rem' }} />
          </div>
        </CardBody>
        <CardFooter>
          <span className="placeholder placeholder-glow bg-darken" style={{ width: '5rem', height: '2.375rem', borderRadius: '0.25rem' }} />
        </CardFooter>
      </Card>
    </>
  );
}

export default Placeholder;
